import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Clock, Route, User, Activity, ChevronRight, ChevronDown, ChevronLeft, Play, Pause, Square, Gauge, Package, Hourglass } from 'lucide-react';
import { vozidloColor } from '../../utils/vozidloColor';

const MONTH_NAMES = ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'];
const formatMonth = (ym) => {
    if (!ym) return '';
    const [year, month] = ym.split('-');
    return `${MONTH_NAMES[Number(month) - 1] || month} ${year}`;
};
const currentMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const shiftMonth = (ym, delta) => {
    const [y, m] = ym.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const SECTION_COLOR = '#0CC0DF';

const SPEED_OPTIONS = [1, 4, 16];

/**
 * Mini-dlaždice objednávek vázané na playback čas. Stav per objednávka
 * se odvíjí od `currentTimeMs` (čas v jízdě, kde je marker přehrávače):
 *   - currentTime < prijezd_at      → na cestě (modrá)
 *   - prijezd_at ≤ ... ≤ doruceni  → u zákazníka (fialová, simulovaný counter)
 *   - currentTime > cas_doruceni    → doručeno (zelený / červený dle late)
 *
 * Číslování je stabilní z backendu (poradi_plan = 1..N podle skutečného
 * pořadí dokončení), takže během přehrávání si nepřeskakují čísla.
 */
function PlaybackOrders({ objednavky, currentTimeMs, vozidloId, isDark }) {
    if (!Array.isArray(objednavky) || objednavky.length === 0) return null;

    const subtle = isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)';
    const carColor = vozidloColor(vozidloId);

    const fmtTime = (iso) => {
        if (!iso) return '—';
        try { return new Date(iso).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }); }
        catch { return '—'; }
    };
    const fmtMmSs = (sec) => {
        if (sec < 0 || !Number.isFinite(sec)) return '0:00';
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec) % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {objednavky.map((o) => {
                const prijezdMs = o.prijezd_at ? new Date(o.prijezd_at).getTime() : null;
                const doruceniMs = o.cas_doruceni ? new Date(o.cas_doruceni).getTime() : null;
                const slibenyMs = o.slibeny_at ? new Date(o.slibeny_at).getTime() : null;
                const late = (o.lateness_min || 0) > 0;

                let phase = 'naceste';
                if (doruceniMs && currentTimeMs > doruceniMs) phase = 'dorucena';
                else if (prijezdMs && currentTimeMs >= prijezdMs) phase = 'uzakaznika';

                const dorucena = phase === 'dorucena';
                const uZakaznika = phase === 'uzakaznika';
                const naceste = phase === 'naceste';

                const bg = uZakaznika
                    ? 'rgba(151,90,205,0.12)'
                    : late && dorucena
                    ? 'rgba(239,68,68,0.06)'
                    : (isDark ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.025)');
                const borderCol = uZakaznika
                    ? 'rgba(151,90,205,0.45)'
                    : (isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-subtle)');

                const renderStatusBadge = () => {
                    if (uZakaznika) {
                        const waitSec = (currentTimeMs - prijezdMs) / 1000;
                        return (
                            <span style={{ display: 'inline-flex', flexDirection: 'column', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                                <span style={{ color: subtle, fontSize: 9 }}>u zákazníka</span>
                                <span style={{
                                    fontWeight: 700, fontSize: 11,
                                    padding: '1px 6px', borderRadius: 999,
                                    background: 'rgba(151,90,205,0.18)',
                                    color: '#975ACD',
                                    border: '1px solid rgba(151,90,205,0.40)',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                }}>
                                    <Hourglass size={9} /> {fmtMmSs(waitSec)}
                                </span>
                            </span>
                        );
                    }
                    if (dorucena) {
                        return (
                            <span style={{ display: 'inline-flex', flexDirection: 'column', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                                <span style={{ color: subtle, fontSize: 9 }}>doručeno</span>
                                <span style={{
                                    fontWeight: 700, fontSize: 11,
                                    padding: '1px 6px', borderRadius: 999,
                                    background: late ? 'rgba(239,68,68,0.15)' : 'rgba(16,185,129,0.15)',
                                    color: late ? '#EF4444' : '#10B981',
                                    border: `1px solid ${late ? 'rgba(239,68,68,0.30)' : 'rgba(16,185,129,0.30)'}`,
                                    whiteSpace: 'nowrap',
                                }}>
                                    {fmtTime(o.cas_doruceni)}
                                    {late && ` +${o.lateness_min}m`}
                                </span>
                            </span>
                        );
                    }
                    // naceste
                    return (
                        <span style={{ display: 'inline-flex', flexDirection: 'column', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                            <span style={{ color: subtle, fontSize: 9 }}>naceste</span>
                            <span style={{
                                color: isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)',
                                fontWeight: 600, fontSize: 11,
                            }}>
                                {fmtTime(o.cas_doruceni)}
                            </span>
                        </span>
                    );
                };

                return (
                    <div key={o.id} style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '5px 7px', borderRadius: 6,
                        background: bg, border: `1px solid ${borderCol}`,
                        fontSize: 10,
                        opacity: naceste ? 0.6 : 1,
                        transition: 'opacity 0.2s, background 0.2s',
                    }}>
                        <span style={{
                            flexShrink: 0,
                            width: 18, height: 18, borderRadius: '50%',
                            background: dorucena ? `${carColor}88` : carColor, color: '#fff',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700, fontSize: 10,
                        }}>
                            {o.poradi_plan ?? '?'}
                        </span>
                        <span style={{ display: 'inline-flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                            <span style={{
                                color: subtle, fontSize: 9,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                                {o.zakaznik_jmeno || `slíbeno ${fmtTime(o.slibeny_at)}`}
                            </span>
                            <span style={{
                                color: isDark ? '#fff' : 'var(--text-primary)',
                                fontWeight: 600, fontSize: 11,
                                fontVariantNumeric: 'tabular-nums',
                            }}>
                                {fmtTime(o.slibeny_at)}
                            </span>
                        </span>
                        {renderStatusBadge()}
                        <span style={{ textAlign: 'right', color: subtle, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                            <div style={{ fontSize: 10 }}>
                                {o.pocet_polozek > 0 ? `${o.pocet_polozek} ks` : '—'}
                            </div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: isDark ? '#fff' : 'var(--text-primary)' }}>
                                {Math.round(o.celkem_s_dph || 0).toLocaleString('cs-CZ')} Kč
                            </div>
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function PlaybackControls({ playback, isDark }) {
    const { body, progress, playing, speed, currentPoint, seek, play, pause, stop, setSpeed } = playback;
    if (!currentPoint) return null;

    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const subtle = isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)';
    const textColor = isDark ? '#fff' : 'var(--text-primary)';
    const containerBg = isDark ? 'rgba(12,192,223,0.06)' : 'rgba(12,192,223,0.04)';
    const containerBorder = isDark ? 'rgba(12,192,223,0.20)' : 'rgba(12,192,223,0.25)';
    const buttonBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
    const buttonBgHover = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)';

    const fmtTime = (s) => s ? new Date(s).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
    const startAt = body[0]?.zaznameno_at;
    const endAt = body[body.length - 1]?.zaznameno_at;
    const totalSec = startAt && endAt ? Math.max(1, (new Date(endAt) - new Date(startAt)) / 1000) : 0;
    const elapsedSec = progress * totalSec;
    const fmtElapsed = (sec) => {
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    const btn = (children, onClick, active = false, disabled = false) => (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                background: active ? 'rgba(12,192,223,0.20)' : buttonBg,
                border: `1px solid ${active ? 'rgba(12,192,223,0.40)' : 'transparent'}`,
                borderRadius: 6,
                padding: '6px 10px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                color: active ? SECTION_COLOR : textColor,
                fontSize: 11, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 4,
                opacity: disabled ? 0.4 : 1,
                transition: 'background 0.12s',
            }}
            onMouseEnter={(e) => { if (!disabled && !active) e.currentTarget.style.background = buttonBgHover; }}
            onMouseLeave={(e) => { if (!disabled && !active) e.currentTarget.style.background = buttonBg; }}
        >
            {children}
        </button>
    );

    return (
        <div style={{
            background: containerBg,
            border: `1px solid ${containerBorder}`,
            borderRadius: 7,
            padding: '10px 12px',
            display: 'flex', flexDirection: 'column', gap: 8,
        }}>
            {/* Aktuální stav: čas + rychlost auta v daný okamžik */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: 11, color: subtle,
                fontVariantNumeric: 'tabular-nums',
            }}>
                <span style={{ color: textColor, fontWeight: 600 }}>
                    {fmtTime(currentPoint.zaznameno_at)}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Gauge size={11} /> {Math.round(currentPoint.rychlost_kmh ?? 0)} km/h
                </span>
                <span>
                    {fmtElapsed(elapsedSec)} / {fmtElapsed(totalSec)}
                </span>
            </div>

            {/* Timeline slider */}
            <input
                type="range"
                min="0"
                max="1000"
                value={Math.round(progress * 1000)}
                onChange={(e) => seek(Number(e.target.value) / 1000)}
                style={{
                    width: '100%',
                    accentColor: SECTION_COLOR,
                    cursor: 'pointer',
                }}
            />

            {/* Controls row: play/pause/stop + speed */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {playing
                    ? btn(<><Pause size={12} /> Pauza</>, pause)
                    : btn(<><Play size={12} /> {progress >= 1 ? 'Přehrát znovu' : 'Přehrát'}</>, play)}
                {btn(<Square size={12} />, stop, false, progress === 1 && !playing)}
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 10, color: labelColor, marginRight: 2 }}>Rychlost:</span>
                {SPEED_OPTIONS.map((s) => (
                    <React.Fragment key={s}>
                        {btn(`${s}×`, () => setSpeed(s), speed === s)}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

/**
 * Kniha jízd vozidla. Načítá `/api/flotila/gps/jizdy?vozidlo_id=X` a
 * skládá je do collapsible skupin po dnech (default zavřené, aby seznam
 * stovek jízd byl použitelný). Klik na jízdu vybere ji jako "historicky
 * zobrazenou" — mapa pak místo live polohy markerů kreslí trasu této jízdy
 * (řízeno z Flotila.jsx).
 */
export default function InspectorJizdy({ vozidlo, selectedJizdaId, onSelectJizda, isDark, playback = null }) {
    const [jizdy, setJizdy] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDays, setOpenDays] = useState(() => new Set()); // klíč = "YYYY-MM-DD"
    const [mesic, setMesic] = useState(currentMonth);
    const [mesiceData, setMesiceData] = useState([]); // [{ mesic, pocet }]
    const [playbackOrders, setPlaybackOrders] = useState([]);

    // Načti objednávky vybrané jízdy pro playback timeline. Stejný endpoint
    // jako u live mapy (HistorickaJizdaLayer), ale výsledek používáme pro
    // mini-dlaždice pod přehrávačem — stav per řádek se mění s `currentPoint`.
    useEffect(() => {
        if (!selectedJizdaId) { setPlaybackOrders([]); return; }
        let cancelled = false;
        fetch(`/api/flotila/gps/jizdy/${selectedJizdaId}/objednavky`)
            .then((r) => r.json())
            .then((d) => { if (!cancelled && Array.isArray(d)) setPlaybackOrders(d); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [selectedJizdaId]);

    // Reset na aktuální měsíc při přepnutí auta
    useEffect(() => { setMesic(currentMonth()); }, [vozidlo?.id]);

    // Načíst dostupné měsíce (s počty jízd) pro picker
    useEffect(() => {
        if (!vozidlo?.id) return;
        fetch(`/api/flotila/gps/jizdy-mesice?vozidlo_id=${vozidlo.id}`)
            .then((r) => r.json())
            .then((d) => setMesiceData(Array.isArray(d) ? d : []))
            .catch(() => setMesiceData([]));
    }, [vozidlo?.id]);

    // Načíst jízdy pro vybraný měsíc
    useEffect(() => {
        if (!vozidlo?.id) return;
        setLoading(true);
        fetch(`/api/flotila/gps/jizdy?vozidlo_id=${vozidlo.id}&mesic=${mesic}`)
            .then((r) => r.json())
            .then((d) => setJizdy(Array.isArray(d) ? d : []))
            .catch(() => setJizdy([]))
            .finally(() => setLoading(false));
    }, [vozidlo?.id, mesic]);

    const mesicMap = useMemo(() => {
        const m = new Map();
        for (const x of mesiceData) m.set(x.mesic, Number(x.pocet) || 0);
        return m;
    }, [mesiceData]);

    // Min/max měsíc dle dostupných dat — používáme pro disabled stav navigace
    const minMesic = mesiceData.length > 0 ? mesiceData[mesiceData.length - 1].mesic : null;
    const maxMesic = mesiceData.length > 0 ? mesiceData[0].mesic : currentMonth();
    const canGoPrev = minMesic && mesic > minMesic;
    const canGoNext = mesic < maxMesic || mesic < currentMonth();

    // Seskupení po dnech (chronologicky DESC — nejnovější den nahoře).
    const dayGroups = useMemo(() => {
        const map = new Map();
        for (const j of jizdy) {
            if (!j.zacatek_at) continue;
            const dayKey = j.zacatek_at.slice(0, 10); // "YYYY-MM-DD"
            if (!map.has(dayKey)) map.set(dayKey, []);
            map.get(dayKey).push(j);
        }
        // Map → seřazené pole
        return Array.from(map.entries())
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([day, items]) => ({
                day,
                items,
                count: items.length,
                totalKm: items.reduce((s, x) => s + Number(x.vzdalenost_km || 0), 0),
                totalObjednavek: items.reduce((s, x) => s + Number(x.objednavky_count || 0), 0),
                hasProbiha: items.some((x) => x.stav === 'probiha'),
            }));
    }, [jizdy]);

    // Auto-otevření dne, který obsahuje aktivně vybranou jízdu (jinak by user
    // nevěděl, kde jeho výběr je). Plus auto-otevření prvního (= aktuálního) dne
    // při prvním načtení, aby user měl hned co vidět.
    useEffect(() => {
        if (loading || dayGroups.length === 0) return;
        setOpenDays((prev) => {
            const next = new Set(prev);
            // Den s vybranou jízdou
            if (selectedJizdaId) {
                const found = jizdy.find((j) => j.id === selectedJizdaId);
                if (found?.zacatek_at) next.add(found.zacatek_at.slice(0, 10));
            }
            // První den default otevřený
            if (next.size === 0) next.add(dayGroups[0].day);
            return next;
        });
    }, [loading, dayGroups, selectedJizdaId, jizdy]);

    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const subtle = isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)';
    const textColor = isDark ? '#fff' : 'var(--text-primary)';
    const dayHeaderBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
    const dayHeaderHover = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)';
    const itemBg = isDark ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.025)';
    const itemBgHover = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
    const itemBgActive = 'rgba(12,192,223,0.12)';
    const itemBorderActive = 'rgba(12,192,223,0.45)';

    const fmtDayHeader = (yyyymmdd) => {
        const d = new Date(yyyymmdd + 'T00:00:00');
        const dayName = d.toLocaleDateString('cs-CZ', { weekday: 'short' });
        const date = d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
        return `${dayName.charAt(0).toUpperCase()}${dayName.slice(1)} · ${date}`;
    };
    const fmtTime = (s) => s ? new Date(s).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }) : '—';
    const fmtMin = (m) => {
        if (!m && m !== 0) return '—';
        const h = Math.floor(m / 60);
        const min = m % 60;
        return h > 0 ? `${h} h ${min} min` : `${min} min`;
    };

    const toggleDay = (day) => {
        setOpenDays((prev) => {
            const next = new Set(prev);
            next.has(day) ? next.delete(day) : next.add(day);
            return next;
        });
    };

    const navBtn = (icon, onClick, disabled) => (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                width: 28, height: 28, borderRadius: 6,
                background: disabled ? 'transparent' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'),
                color: disabled ? labelColor : textColor,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.4 : 1,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            {icon}
        </button>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Měsíční picker — šipky + label, plus dropdown pro skok na konkrétní měsíc */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 8px', borderRadius: 7,
                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`,
            }}>
                {navBtn(<ChevronLeft size={14} />, () => setMesic((m) => shiftMonth(m, -1)), !canGoPrev)}
                <select
                    value={mesic}
                    onChange={(e) => setMesic(e.target.value)}
                    style={{
                        flex: 1,
                        background: 'transparent',
                        color: textColor,
                        border: 'none',
                        fontSize: 13, fontWeight: 600,
                        textAlign: 'center',
                        cursor: 'pointer',
                        outline: 'none',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {/* Když vybraný měsíc nemá data, stejně ho zobrazíme jako možnost */}
                    {!mesicMap.has(mesic) && (
                        <option value={mesic} style={{ background: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#111' }}>
                            {formatMonth(mesic)} (0)
                        </option>
                    )}
                    {mesiceData.map((x) => (
                        <option key={x.mesic} value={x.mesic} style={{ background: isDark ? '#1a1a1a' : '#fff', color: isDark ? '#fff' : '#111' }}>
                            {formatMonth(x.mesic)} ({x.pocet})
                        </option>
                    ))}
                </select>
                {navBtn(<ChevronRight size={14} />, () => setMesic((m) => shiftMonth(m, 1)), !canGoNext)}
            </div>

            {selectedJizdaId && (
                <>
                    <button
                        type="button"
                        onClick={() => onSelectJizda?.(null)}
                        style={{
                            padding: '8px 12px', borderRadius: 6,
                            background: 'rgba(255,138,0,0.10)',
                            border: '1px solid rgba(255,138,0,0.30)',
                            color: '#FF8A00',
                            fontSize: 12, fontWeight: 600,
                            cursor: 'pointer',
                        }}
                    >
                        ← Zpět na live
                    </button>
                    {playback && playback.body.length > 1 && (
                        <PlaybackControls playback={playback} isDark={isDark} />
                    )}
                    {/* Objednávky této jízdy — stav per řádek se mění s playback timeline */}
                    {playbackOrders.length > 0 && playback?.currentPoint?.zaznameno_at && (
                        <PlaybackOrders
                            objednavky={playbackOrders}
                            currentTimeMs={new Date(playback.currentPoint.zaznameno_at).getTime()}
                            vozidloId={vozidlo?.id}
                            isDark={isDark}
                        />
                    )}
                </>
            )}

            {loading && (
                <div style={{ padding: 16, textAlign: 'center', color: labelColor, fontSize: 12 }}>Načítám…</div>
            )}
            {!loading && jizdy.length === 0 && (
                <div style={{ padding: 16, textAlign: 'center', color: labelColor, fontSize: 12 }}>Žádné jízdy v tomto měsíci</div>
            )}

            {!loading && dayGroups.map((g) => {
                const open = openDays.has(g.day);
                return (
                    <div key={g.day} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <button
                            type="button"
                            onClick={() => toggleDay(g.day)}
                            style={{
                                background: dayHeaderBg,
                                border: 'none',
                                borderRadius: 6,
                                padding: '8px 10px',
                                cursor: 'pointer',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                transition: 'background 0.12s',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = dayHeaderHover; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = dayHeaderBg; }}
                        >
                            {open
                                ? <ChevronDown size={14} style={{ color: subtle, flexShrink: 0 }} />
                                : <ChevronRight size={14} style={{ color: subtle, flexShrink: 0 }} />}
                            <span style={{
                                flex: 1,
                                fontSize: 12, fontWeight: 700,
                                color: textColor,
                                fontVariantNumeric: 'tabular-nums',
                            }}>
                                {fmtDayHeader(g.day)}
                            </span>
                            {g.hasProbiha && (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    padding: '1px 5px', borderRadius: 3,
                                    background: 'rgba(16,185,129,0.15)',
                                    color: '#10B981',
                                    border: '1px solid rgba(16,185,129,0.30)',
                                    fontSize: 8, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: 0.3,
                                }}>
                                    <Activity size={8} /> Jede
                                </span>
                            )}
                            <span style={{ fontSize: 11, color: subtle, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                {g.count}× · {g.totalKm.toFixed(0)} km{g.totalObjednavek > 0 ? ` · ${g.totalObjednavek} obj.` : ''}
                            </span>
                        </button>

                        {open && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 6 }}>
                                {g.items.map((j) => {
                                    const isActive = j.id === selectedJizdaId;
                                    const isProbiha = j.stav === 'probiha';
                                    const ridic = j.zamestnanec ? `${j.zamestnanec.jmeno || ''} ${j.zamestnanec.prijmeni || ''}`.trim() : null;
                                    return (
                                        <button
                                            key={j.id}
                                            type="button"
                                            onClick={() => onSelectJizda?.(j.id)}
                                            style={{
                                                background: isActive ? itemBgActive : itemBg,
                                                border: `1px solid ${isActive ? itemBorderActive : 'transparent'}`,
                                                borderRadius: 6,
                                                padding: '8px 10px',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 4,
                                                transition: 'background 0.12s, border-color 0.12s',
                                            }}
                                            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = itemBgHover; }}
                                            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = itemBg; }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                                <span style={{
                                                    fontSize: 12, fontWeight: 600, color: textColor,
                                                    fontVariantNumeric: 'tabular-nums',
                                                }}>
                                                    {fmtTime(j.zacatek_at)}
                                                    {j.konec_at && <span style={{ color: labelColor }}> → {fmtTime(j.konec_at)}</span>}
                                                </span>
                                                {isProbiha && (
                                                    <span style={{
                                                        padding: '1px 5px', borderRadius: 3,
                                                        background: 'rgba(16,185,129,0.15)',
                                                        color: '#10B981',
                                                        border: '1px solid rgba(16,185,129,0.30)',
                                                        fontSize: 8, fontWeight: 700,
                                                        textTransform: 'uppercase', letterSpacing: 0.3,
                                                    }}>
                                                        Jede
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{
                                                display: 'flex', flexWrap: 'wrap', gap: 10,
                                                fontSize: 11, color: subtle,
                                                fontVariantNumeric: 'tabular-nums',
                                            }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                                    <Route size={10} /> {j.vzdalenost_km ? `${Number(j.vzdalenost_km).toFixed(1)} km` : '—'}
                                                </span>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                                    <Clock size={10} /> {fmtMin(j.trvani_min)}
                                                </span>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: ridic ? 'inherit' : labelColor, fontStyle: ridic ? 'normal' : 'italic' }}>
                                                    <User size={10} /> {ridic || 'Bez řidiče'}
                                                </span>
                                                {(j.objednavky_count > 0) && (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                                        <Package size={10} /> {j.objednavky_count} obj.
                                                    </span>
                                                )}
                                                {(j.udalosti_count > 0) && (
                                                    <span style={{ color: '#FFC857' }}>
                                                        {j.udalosti_count} událost{j.udalosti_count === 1 ? '' : (j.udalosti_count < 5 ? 'i' : 'í')}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
